import { addDays, endOfMonth, format, parseISO, startOfMonth, startOfQuarter, startOfYear, subMonths } from 'date-fns';
import { CsvValue, ICsvColumn, toCsv } from '../denials/denials-export';
import { IBankAccount, IBankingApi, IBankTransaction } from './banking-api';
import { IRemit, IRemitsApi, RemitKind } from './remits-api';

/**
 * Export reconciled transactions (FE-163, crosswalk §6): every bank deposit posted to an ERA or EOB in a date range, with
 * the remit it was posted to, as a CSV for accounting. Built in the browser from the list calls; nothing is written.
 *
 * Two corrections to the crosswalk, both measured on production 2026-10-09:
 * - the date range is ONE `BT:from:to` value — repeated `GTE`/`LTE` values are OR'ed and return every posted deposit;
 * - Plaid stores money in as a NEGATIVE amount, so the Deposit Amount column is the amount negated — a deposit is
 *   positive, and Variance (deposit − remit paid) is zero when they match;
 * - paging is not stable: the list's sort has no tie-breaker, so reading September's 236 posted deposits 100 at a time
 *   by date returned 236 rows but only 233 distinct ones — three twice, three never. The export therefore reads ONE DAY
 *   per call (a day fits a page), and says so if a day ever does not.
 */

export type ExportPreset = 'thisMonth' | 'lastMonth' | 'thisQuarter' | 'yearToDate' | 'custom';

export const EXPORT_PRESETS: Array<{ id: ExportPreset; label: string }> = [
    { id: 'thisMonth', label: 'This month' },
    { id: 'lastMonth', label: 'Last month' },
    { id: 'thisQuarter', label: 'This quarter' },
    { id: 'yearToDate', label: 'Year to date' },
];

const iso = (date: Date): string => format(date, 'yyyy-MM-dd');

/** The preset's days, both included, from today's date on this machine. Custom has none of its own. */
export const exportPresetRange = (preset: ExportPreset, today: Date): { from: string; to: string } | undefined => {
    switch (preset) {
        case 'thisMonth':
            return { from: iso(startOfMonth(today)), to: iso(today) };
        case 'lastMonth': {
            const last = subMonths(today, 1);
            return { from: iso(startOfMonth(last)), to: iso(endOfMonth(last)) };
        }
        case 'thisQuarter':
            return { from: iso(startOfQuarter(today)), to: iso(today) };
        case 'yearToDate':
            return { from: iso(startOfYear(today)), to: iso(today) };
        default:
            return undefined;
    }
};

/**
 * One line of the file: a posted deposit and one remit posted to it. A deposit with several remits has a line each, and
 * only the FIRST carries the deposit's amount and variance — so a column total is the bank's, not a multiple of it. A
 * posted deposit whose remit cannot be read has a line of its own with the remit columns blank: for accounting that is
 * the exception row, not one to leave out.
 */
export interface IReconciledRow {
    transaction: IBankTransaction;
    account?: IBankAccount;
    remit?: IRemit;
    clearinghouse?: string;
    /** The first line of its deposit: the one that carries Deposit Amount and Variance. */
    first: boolean;
    /** Deposit − everything posted to it. */
    variance: number;
}

export interface IReconciledExport {
    rows: IReconciledRow[];
    /** Distinct posted deposits in the file. */
    deposits: number;
    /** Days with more posted deposits than one page holds: read by pages, which can skip a deposit. Normally empty. */
    unsureDays: string[];
    /** Posted deposits whose remit could not be read (deleted, disabled, or on a list this console cannot read). */
    unmatched: number;
    depositsTotal: number;
    /** Net: Σ (deposit − its remits). */
    variance: number;
    /** Deposits whose remits do not add up to them — the ones to look at, whatever the net says. */
    depositsWithVariance: number;
}

const cents = (value: number): number => Math.round(value * 100) / 100;
const two = (value: number | undefined): CsvValue => (value === undefined ? undefined : { fixed: cents(value), decimals: 2 });

/** `Chase Operating`; a manual account is its own name (crosswalk §6, column 2). */
const accountName = (account: IBankAccount | undefined): string | undefined =>
    account
        ? String(account.itemType).toUpperCase() === 'MANUAL'
            ? account.accountName
            : [account.institution, account.accountName].filter(Boolean).join(' ')
        : undefined;

const paid = (remit: IRemit): number => cents(Number(remit.paidAmount || 0));

export const reconciledCsvColumns: Array<ICsvColumn<IReconciledRow>> = [
    { header: 'Bank Transaction Date', value: row => row.transaction.date?.slice(0, 10) },
    { header: 'Bank Account', value: row => accountName(row.account) },
    { header: 'Account Mask', value: row => row.account?.mask },
    { header: 'Bank Transaction ID', value: row => row.transaction.externalId },
    { header: 'Description', value: row => row.transaction.name },
    { header: 'Deposit Amount', value: row => (row.first ? two(row.transaction.deposit) : undefined) },
    { header: 'Remit Type', value: row => row.remit?.kind },
    { header: 'Remit ID', value: row => row.remit?.displayId },
    { header: 'Payer', value: row => row.remit?.payerName },
    { header: 'Payment Number', value: row => row.remit?.paymentNumber },
    { header: 'Payment Type', value: row => row.remit?.paymentType },
    { header: 'Remit Paid Date', value: row => row.remit?.paidDate?.slice(0, 10) },
    { header: 'Remit Paid Amount', value: row => (row.remit ? two(paid(row.remit)) : undefined) },
    { header: 'Variance', value: row => (row.first ? two(row.variance) : undefined) },
    { header: 'Clearinghouse', value: (row): CsvValue => (row.remit?.kind === 'ERA' ? row.clearinghouse : undefined) },
];

export const reconciledCsv = (rows: IReconciledRow[]): string => toCsv(rows, reconciledCsvColumns);

/** `reconciled-transactions_{from}_to_{to}.csv`; `_INCOMPLETE` before the extension when a day may be missing deposits. */
export const reconciledCsvFilename = (from: string, to: string, incomplete = false): string =>
    `reconciled-transactions_${from}_to_${to}${incomplete ? '_INCOMPLETE' : ''}.csv`;

/** Joins posted deposits to the remits that point at them, oldest deposit first. */
export const joinReconciled = (
    transactions: IBankTransaction[],
    remits: IRemit[],
    accounts: IBankAccount[],
    clearinghouseName: (id: string | undefined) => string | undefined,
): Omit<IReconciledExport, 'unsureDays'> => {
    const byTransaction = new Map<string, IRemit[]>();
    remits.forEach(remit => {
        if (remit.plaidItemTransactionId) {
            byTransaction.set(remit.plaidItemTransactionId, [...(byTransaction.get(remit.plaidItemTransactionId) || []), remit]);
        }
    });
    let unmatched = 0;
    let depositsTotal = 0;
    let variance = 0;
    let depositsWithVariance = 0;
    const rows: IReconciledRow[] = [];
    const sorted = [...transactions].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    sorted.forEach(transaction => {
        const posted = byTransaction.get(transaction.id) || [];
        const account = accounts.find(each => each.itemId === transaction.itemId);
        const gap = cents(transaction.deposit - posted.reduce((sum, remit) => sum + paid(remit), 0));
        depositsTotal += transaction.deposit;
        variance += gap;
        if (gap !== 0) {
            depositsWithVariance += 1;
        }
        if (!posted.length) {
            unmatched += 1;
            rows.push({ transaction, account, first: true, variance: gap });
            return;
        }
        posted.forEach((remit, index) =>
            rows.push({
                transaction,
                account,
                remit,
                clearinghouse: clearinghouseName(remit.clearinghouseId),
                first: index === 0,
                variance: gap,
            }),
        );
    });
    return {
        rows,
        deposits: sorted.length,
        unmatched,
        depositsTotal: cents(depositsTotal),
        variance: cents(variance),
        depositsWithVariance,
    };
};

const PAGE = 100;
const IDS_PER_CALL = 50;
const AT_ONCE = 6;

/** Runs the tasks, `AT_ONCE` at a time, in order of the input. */
const pooled = async <T>(tasks: Array<() => Promise<T>>): Promise<T[]> => {
    const results: T[] = new Array(tasks.length);
    let next = 0;
    await Promise.all(
        Array.from({ length: Math.min(AT_ONCE, tasks.length) }, async () => {
            while (next < tasks.length) {
                const index = next++;
                results[index] = await tasks[index]();
            }
        }),
    );
    return results;
};

/**
 * Everything the file needs: the posted deposits in the range for each chosen account, a day per call, then the remits
 * posted to them — fifty transactions a call, which the API ORs (60 in one call measured exact, 2026-10-09).
 */
export const collectReconciled = async ({
    banking,
    remits,
    accounts,
    from,
    to,
    clearinghouseName,
    onProgress,
}: {
    banking: IBankingApi;
    remits: IRemitsApi;
    accounts: IBankAccount[];
    from: string;
    to: string;
    clearinghouseName: (id: string | undefined) => string | undefined;
    onProgress?: (done: number, total: number) => void;
}): Promise<IReconciledExport> => {
    const days: string[] = [];
    for (let day = parseISO(from); format(day, 'yyyy-MM-dd') <= to; day = addDays(day, 1)) {
        days.push(format(day, 'yyyy-MM-dd'));
    }
    const unsureDays = new Set<string>();
    let read = 0;
    const perDay = await pooled(
        accounts.flatMap(account =>
            days.map(day => async (): Promise<IBankTransaction[]> => {
                const rows: IBankTransaction[] = [];
                for (let offset = 0; ; offset += PAGE) {
                    const page = await banking.listTransactions(account.itemId, {
                        tab: 'POSTED',
                        date: day,
                        sort: 'AMOUNT',
                        order: 'ASCENDING',
                        limit: PAGE,
                        offset,
                    });
                    rows.push(...page.rows);
                    if (page.total > PAGE) {
                        unsureDays.add(day);
                    }
                    if (page.rows.length < PAGE || rows.length >= page.total) {
                        break;
                    }
                }
                read += 1;
                onProgress?.(read, accounts.length * days.length);
                return rows;
            }),
        ),
    );
    // One copy of each deposit, should a page have repeated one.
    const transactions = [...new Map(perDay.flat().map(transaction => [transaction.id, transaction])).values()];
    const ids = transactions.map(transaction => transaction.id).filter(Boolean);
    const batches: string[][] = [];
    for (let start = 0; start < ids.length; start += IDS_PER_CALL) {
        batches.push(ids.slice(start, start + IDS_PER_CALL));
    }
    const found = await pooled(
        batches.flatMap(batch =>
            (['ERA', 'EOB'] as RemitKind[]).map(kind => async (): Promise<IRemit[]> => {
                // Every page, though a deposit is on one remit as a rule (30 of 30 measured): a file that silently drops a
                // remit would not add up, and nothing on it would say why.
                const rows: IRemit[] = [];
                for (let offset = 0; ; offset += PAGE) {
                    const page = await remits.listRemits(kind, { limit: PAGE, offset, transactionIds: batch });
                    rows.push(...page.rows);
                    if (page.rows.length < PAGE || rows.length >= page.total) {
                        return rows;
                    }
                }
            }),
        ),
    );
    // One copy of each remit too: a remit is found once per call that names its deposit.
    const unique = [...new Map(found.flat().map(remit => [`${remit.kind}:${remit.id}`, remit])).values()];
    return { ...joinReconciled(transactions, unique, accounts, clearinghouseName), unsureDays: [...unsureDays].sort() };
};
